import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Clock, MessageCircle, Plane, BookOpen, KanbanSquare, Bug, User, CheckCircle2, ArrowRight, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Step {
  title: string;
  Icon: LucideIcon;
  body: React.ReactNode;
  cta?: { label: string; to: string };
}

export function Tutorial() {
  const { employee, refreshEmployee } = useAuth();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [skipping, setSkipping] = useState(false);

  // Don't show for admins, kiosk role, or already-completed users.
  if (!employee) return null;
  if (employee.role === "admin" || employee.role === "super_admin" || employee.role === "kiosk") return null;
  if (employee.tutorial_completed_at) return null;

  const steps: Step[] = [
    {
      title: `Welcome to Sunday State HQ, ${employee.first_name || "team"}!`,
      Icon: CheckCircle2,
      body: (
        <>
          <p>This is your hub for everything Scent A Filter — clocking in and out, requesting time off, chatting with the team, finding SOPs, and more.</p>
          <p className="mt-3 text-sm text-gray2">This walkthrough is required the first time you sign in. It takes about 60 seconds. Let's go.</p>
        </>
      ),
    },
    {
      title: "Clock in and out",
      Icon: Clock,
      body: (
        <>
          <p>Track your hours from the dashboard's <strong>Clock In</strong> button. We'll snap a quick verification photo with the webcam.</p>
          {isMobile ? (
            <p className="mt-3 rounded-lg bg-warn-bg p-3 text-sm text-warn">
              <strong>On mobile?</strong> Clock-in is desktop or warehouse kiosk only — this keeps hours accurate.
            </p>
          ) : (
            <p className="mt-3 text-sm text-gray2">Or use the warehouse <strong>kiosk</strong> on shift — pick your name, enter your PIN, done.</p>
          )}
        </>
      ),
      cta: { label: "Take me there", to: "/time" },
    },
    {
      title: "Time off (currently unpaid)",
      Icon: Plane,
      body: (
        <>
          <p>Need a day off? Submit your dates and reason from <strong>Time Off</strong>. Your manager gets a notification and decides yes or no — you'll get an email either way.</p>
          <p className="mt-3 text-sm text-gray2">Note: time off is currently <strong>unpaid</strong>. We're using this to track approved absences.</p>
        </>
      ),
      cta: { label: "See Time Off", to: "/pto" },
    },
    {
      title: "Chat with the team",
      Icon: MessageCircle,
      body: (
        <>
          <p>Use <strong>Chat</strong> for quick team messages. Direct message anyone, or jump into a channel.</p>
          <p className="mt-3 text-sm text-gray2">Keep work talk in here, not in personal messengers — easier to find things later.</p>
        </>
      ),
      cta: { label: "Open Chat", to: "/chat" },
    },
    {
      title: "SOPs, Resources, and your Profile",
      Icon: BookOpen,
      body: (
        <>
          <p><strong>SOPs</strong> = how we do things. <strong>Resources</strong> = links and tools you'll need.</p>
          <p className="mt-3"><strong>Profile</strong> is where you can update your phone number, emergency contact, and view your documents.</p>
        </>
      ),
    },
    {
      title: "Boards, Goals, and your Shifts",
      Icon: KanbanSquare,
      body: (
        <>
          <p><strong>Boards</strong> = your task lists. <strong>My Goals</strong> = personal goals your manager set. <strong>My Shifts</strong> = your schedule.</p>
          <p className="mt-3 text-sm text-gray2">Check these at the start of each shift.</p>
        </>
      ),
    },
    {
      title: "Something broken? Tell us.",
      Icon: Bug,
      body: (
        <>
          <p>There's a red <strong>Report a bug</strong> button in the bottom-right of every page. Tap it any time something is wrong, missing, or confusing.</p>
          <p className="mt-3 text-sm text-gray2">We read every single one. It's the fastest way to make HQ better for the whole team.</p>
        </>
      ),
    },
    {
      title: "You're all set",
      Icon: User,
      body: (
        <>
          <p>That's the tour. Hit the button below to finish and go to your dashboard.</p>
          <p className="mt-3 text-sm text-gray2">If you forget anything, the sidebar has labels on every section. You can re-explore any time.</p>
        </>
      ),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  async function complete() {
    if (!employee) return;
    setBusy(true);
    const { error } = await supabase.from("hq_employees")
      .update({ tutorial_completed_at: new Date().toISOString() })
      .eq("id", employee.id);
    setBusy(false);
    if (error) { alert(error.message); return; }
    await refreshEmployee();
  }

  async function skipForNow() {
    // We don't actually let them skip — but if they try, give them a soft confirmation.
    if (!confirm("This walkthrough is required the first time you sign in. You can take 60 seconds now or finish it on the dashboard later — but you won't be able to use other pages until it's done. Skip for now?")) return;
    setSkipping(true);
  }

  if (skipping) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 p-4">
        <div className="card w-full max-w-md text-center">
          <p className="mb-4 text-sm">You need to complete the walkthrough before using Sunday State HQ. Ready now?</p>
          <button onClick={() => setSkipping(false)} className="btn">Resume walkthrough</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 p-4">
      <div className="card w-full max-w-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-mint text-teal-dark">
              <current.Icon size={20} />
            </div>
            <div className="text-xs uppercase tracking-wider text-gray2">Step {step + 1} of {steps.length}</div>
          </div>
          <button onClick={skipForNow} className="text-gray2 hover:text-charcoal" title="Try to skip">
            <X size={18} />
          </button>
        </div>

        <h2 className="mb-3 text-xl font-bold">{current.title}</h2>
        <div className="text-sm leading-relaxed text-charcoal">{current.body}</div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {current.cta && (
            <button
              onClick={() => { nav(current.cta!.to); }}
              className="text-sm text-teal-dark underline hover:text-teal"
            >
              {current.cta.label}
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="btn-secondary text-sm">Back</button>
            )}
            {isLast ? (
              <button onClick={() => void complete()} disabled={busy} className="btn flex items-center gap-2 text-sm">
                {busy ? "Saving…" : (<>Finish <CheckCircle2 size={14} /></>)}
              </button>
            ) : (
              <button onClick={() => setStep(s => s + 1)} className="btn flex items-center gap-2 text-sm">
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 h-1 overflow-hidden rounded-full bg-line">
          <div className="h-full bg-teal-dark transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}
